import { useState, useEffect } from 'react';
import { Bell, BellRinging } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { toast } from 'sonner';
import { enablePushNotifications, isPushEnabled } from '../lib/push';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    fetchNotifications();
    checkPushStatus();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  const checkPushStatus = async () => {
    const enabled = await isPushEnabled();
    setPushEnabled(enabled);
  };

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (error) {
      // Silent fail
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      // Silent fail
    }
  };

  const handleEnablePush = async () => {
    try {
      await enablePushNotifications();
      setPushEnabled(true);
      toast.success('Push notifications enabled!');
    } catch (error) {
      toast.error(error.message || 'Failed to enable notifications');
    }
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen && unreadCount > 0) {
      setTimeout(markAllRead, 1000);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-full relative" data-testid="notification-bell">
          {unreadCount > 0 ? (
            <BellRinging size={20} weight="fill" className="text-primary" />
          ) : (
            <Bell size={20} />
          )}
          {unreadCount > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-primary text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center"
              data-testid="notification-unread-count"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[calc(100vw-2rem)] sm:w-80 p-0 bg-black/95 border-white/10 backdrop-blur-2xl" align="end" sideOffset={8}>
        <div className="border-b border-white/10 p-4 flex items-center justify-between">
          <h4 className="font-medium">Notifications</h4>
          {!pushEnabled && (
            <Button
              onClick={handleEnablePush}
              size="sm"
              variant="ghost"
              className="text-xs text-primary hover:text-primary"
              data-testid="enable-push-button"
            >
              Enable Push
            </Button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="no-notifications">
              No notifications yet
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={`p-4 border-b border-white/5 hover:bg-white/5 transition-colors ${
                  !notif.read ? 'bg-primary/5' : ''
                }`}
                data-testid={`notification-item-${notif.id}`}
              >
                <div className="text-sm font-medium mb-1">{notif.title}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{notif.body}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {new Date(notif.created_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
